import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from '../utils/safeToast';
import { 
  Download, Upload, Loader2, Database, Shield, AlertTriangle, 
  CheckCircle, HardDrive, Clock, FileArchive, RefreshCw 
} from 'lucide-react';

const API = "/api";

const BackupPage = () => {
  const [loading, setLoading] = useState(true);
  const [backupInfo, setBackupInfo] = useState(null);
  const [localBackups, setLocalBackups] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [restoreStats, setRestoreStats] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchBackupInfo();
    fetchServerBackups();
  }, []);

  const fetchBackupInfo = async () => {
    try {
      const res = await axios.get(`${API}/admin/backup/info`);
      setBackupInfo(res.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Backup-Informationen');
    } finally {
      setLoading(false);
    }
  };

  const fetchServerBackups = async () => {
    try {
      const res = await axios.get(`${API}/admin/backup/list`);
      setLocalBackups(res.data.backups || []);
    } catch (error) {
      console.error('Error fetching server backups:', error);
    }
  };

  const handleDownloadBackup = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/admin/backup`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'gb_sylt_backup.zip';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup erfolgreich in Ihren Download-Ordner geladen');
    } catch (error) {
      toast.error('Fehler beim Herunterladen des Backups');
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.zip') && !file.name.endsWith('.json')) {
        toast.error('Bitte nur .zip oder .json Dateien auswählen');
        return;
      }
      setSelectedFile(file);
      setRestoreDialogOpen(true);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile) return;
    
    setRestoreDialogOpen(false);
    setConfirmDialogOpen(true);
  };

  const executeRestore = async () => {
    if (!selectedFile) return;
    
    setRestoring(true);
    setConfirmDialogOpen(false);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const res = await axios.post(`${API}/admin/backup/restore?confirm=true`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setRestoreStats(res.data);
      toast.success('Backup erfolgreich wiederhergestellt!');
      
      // Refresh data
      fetchBackupInfo();
      
      // Show success message and suggest reload
      setTimeout(() => {
        if (window.confirm('Backup wiederhergestellt. Seite neu laden, um alle Änderungen zu sehen?')) {
          window.location.reload();
        }
      }, 1000);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Wiederherstellen');
    } finally {
      setRestoring(false);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="backup-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#0E4C92]" />
            Backup & Restore
          </h1>
          <p className="text-stone-500 mt-1">Datensicherung und Wiederherstellung</p>
        </div>
        <Badge variant="outline" className="text-[#0E4C92] border-[#0E4C92]">
          Version {backupInfo?.version || '1.0'}
        </Badge>
      </div>

      {/* Warning Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-medium text-amber-800">Wichtiger Hinweis</p>
          <p className="text-sm text-amber-700 mt-1">
            Erstellen Sie regelmäßig Backups, besonders vor Updates oder größeren Änderungen. 
            Ein Restore überschreibt alle vorhandenen Daten unwiderruflich!
          </p>
        </div>
      </div>

      {/* Current Database Status */}
      <div className="bg-white border border-stone-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
          <Database className="w-5 h-5 text-[#0E4C92]" />
          Aktuelle Datenbank
        </h2>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {backupInfo?.collections && Object.entries(backupInfo.collections).map(([key, value]) => (
            <div key={key} className="bg-stone-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-[#0E4C92]">{value.count}</p>
              <p className="text-sm text-stone-600">{value.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Backup Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Download Backup - Lokaler Download auf Nutzer-PC */}
        <div className="bg-white border border-stone-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-2">
            <Download className="w-5 h-5 text-emerald-600" />
            Backup herunterladen (lokal)
          </h3>
          <p className="text-sm text-stone-600 mb-4">
            Erstellt eine vollständige ZIP-Datei und lädt sie auf Ihren Computer herunter. 
            Die Datei landet in Ihrem Download-Ordner.
          </p>
          <Button
            onClick={handleDownloadBackup}
            disabled={downloading}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
            data-testid="download-backup-btn"
          >
            {downloading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Wird erstellt...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                ZIP herunterladen
              </>
            )}
          </Button>
        </div>

        {/* Restore Backup */}
        <div className="bg-white border border-stone-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-2">
            <Upload className="w-5 h-5 text-orange-600" />
            Backup wiederherstellen
          </h3>
          <p className="text-sm text-stone-600 mb-4">
            Stellt alle Daten aus einer Backup-Datei wieder her. <span className="text-red-600 font-medium">Überschreibt vorhandene Daten!</span>
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip,.json"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="backup-file-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={restoring}
            variant="outline"
            className="w-full border-orange-300 text-orange-700 hover:bg-orange-50 rounded-full"
            data-testid="restore-backup-btn"
          >
            {restoring ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Wird wiederhergestellt...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Backup-Datei auswählen
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Automatische Server-Backups */}
      <div className="bg-white border border-stone-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-[#0E4C92]" />
            Automatische Server-Backups
          </h3>
          <Badge variant="outline" className="text-emerald-600 border-emerald-300">
            <Clock className="w-3 h-3 mr-1" />
            Täglich 03:00 Uhr
          </Badge>
        </div>
        
        <p className="text-sm text-stone-600 mb-4">
          Backups werden automatisch auf dem Server erstellt und rotiert. 
          Die letzten 14 Backups werden aufbewahrt.
        </p>
        
        {localBackups.length === 0 ? (
          <div className="text-center py-8 text-stone-500">
            <FileArchive className="w-12 h-12 mx-auto mb-2 text-stone-300" />
            <p>Noch keine automatischen Backups vorhanden</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Dateiname</TableHead>
                <TableHead>Erstellt am</TableHead>
                <TableHead>Größe</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {localBackups.map((backup, index) => (
                <TableRow key={backup.filename}>
                  <TableCell className="font-mono text-sm">
                    {backup.filename}
                    {index === 0 && (
                      <Badge className="ml-2 bg-emerald-100 text-emerald-700">Neuestes</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-stone-600">
                    <Clock className="w-4 h-4 inline mr-1" />
                    {formatDate(backup.created_at)}
                  </TableCell>
                  <TableCell className="text-stone-600">{formatBytes(backup.size_bytes)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Restore Stats (after successful restore) */}
      {restoreStats && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-emerald-800 flex items-center gap-2 mb-4">
            <CheckCircle className="w-5 h-5" />
            Wiederherstellung erfolgreich
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {restoreStats.stats && Object.entries(restoreStats.stats).map(([key, count]) => (
              <div key={key} className="text-center">
                <p className="text-xl font-bold text-emerald-700">{count}</p>
                <p className="text-sm text-emerald-600 capitalize">{key.replace('_', ' ')}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-emerald-600 mt-4">
            Backup vom: {restoreStats.backup_created_at ? formatDate(restoreStats.backup_created_at) : 'Unbekannt'}
          </p>
        </div>
      )}

      {/* Restore Confirmation Dialog */}
      <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-orange-600" />
              Backup wiederherstellen
            </DialogTitle>
            <DialogDescription>
              Sie haben folgende Datei ausgewählt:
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-stone-50 rounded-lg p-4 flex items-center gap-3">
              <FileArchive className="w-10 h-10 text-[#0E4C92]" />
              <div>
                <p className="font-medium text-slate-900">{selectedFile?.name}</p>
                <p className="text-sm text-stone-500">{selectedFile && formatBytes(selectedFile.size)}</p>
              </div>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => { setRestoreDialogOpen(false); setSelectedFile(null); }} className="flex-1 rounded-full">
              Abbrechen
            </Button>
            <Button onClick={handleRestore} className="flex-1 bg-orange-600 hover:bg-orange-700 rounded-full">
              Weiter
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Final Confirmation Dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Warnung: Alle Daten werden überschrieben!
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Diese Aktion kann <strong>nicht rückgängig</strong> gemacht werden. 
                Alle aktuellen Daten werden durch das Backup ersetzt:
              </p>
              <ul className="list-disc list-inside text-sm text-stone-600">
                <li>Objekte, Eigentümer, Firmen</li>
                <li>Aufgaben und Historie</li>
                <li>Tags und Einstellungen</li>
                <li>Benutzerkonten</li>
              </ul>
              <p className="font-medium text-red-600 mt-4">
                Sind Sie absolut sicher, dass Sie fortfahren möchten?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={executeRestore}
              className="bg-red-600 hover:bg-red-700 rounded-full"
              data-testid="confirm-restore-btn"
            >
              Ja, Backup wiederherstellen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BackupPage;
